import React from "react";
import { useCallback } from "react";
import { AdvancedSearchFormValues } from "services/search";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { MultiDropdownOption } from "ui/components/Dropdown/MultiDropdown/types";
import { RadioButtonOption } from "ui/components/RadioButton/types";

const getAdvancedSearch = (
  current: AdvancedSearchFormValues,
  advancedSearch: AdvancedSearchFormValues[]
) => {
  let updatedAdvancedSearch: AdvancedSearchFormValues[] = [];

  if (advancedSearch) {
    const exists = advancedSearch.some((av) => av.name === current.name);

    if (exists) {
      // Ažuriraj samo element koji odgovara fieldName
      updatedAdvancedSearch = advancedSearch.map(
        (av) =>
          av.name === current.name
            ? { ...av, ...current } // ažuriraj samo odgovarajući element
            : av // ostali ostaju isti
      );
    } else {
      updatedAdvancedSearch = [...advancedSearch, current]; // Ako ne postoji, dodajemo
    }

    // Filtriraj elemente koji imaju value null ili prazan string ''
    updatedAdvancedSearch = updatedAdvancedSearch.filter(
      (av) => av.value !== null && av.value !== ""
    );
  } else {
    updatedAdvancedSearch = [current]; // Ako advancedSearch nije definirano, samo dodajemo
  }

  return updatedAdvancedSearch;
};

export const useHandleTextFieldChange = <T>(
  setFormValues: (newFormValues: T) => void,
  formValues: T,
  advancedSearch?: AdvancedSearchFormValues[],
  doNotRound?: boolean
) =>
  useCallback(
    (e: any) => {
      const newValue = e.target.value;
      const fieldName = e.target.name;
      const isNumber = e.target.type === "number";

      const advancedSearchFormValue: AdvancedSearchFormValues = {
        name: fieldName,
        label: e.target.dataset?.label,   // <-- FIXED
        value: newValue,
        text: newValue,
      };

      const updatedAdvancedSearch = getAdvancedSearch(
        advancedSearchFormValue,
        advancedSearch ?? []
      );

      if (isNumber) {
        const resolvedNumberValue =
          newValue === ""
            ? null
            : doNotRound
              ? newValue
              : parseInt(newValue, 10);

        setFormValues({
          ...formValues,
          advancedSearch: updatedAdvancedSearch,
          [fieldName]: resolvedNumberValue,
        });
        return;
      }

      setFormValues({
        ...formValues,
        advancedSearch: updatedAdvancedSearch,
        [fieldName]: newValue === "" ? null : newValue,
      });
    },
    [setFormValues, formValues, advancedSearch, doNotRound]
  );

export const useHandleNumberFieldChange = <T extends Record<string, any>>(
  setFormValues: (newFormValues: T) => void,
  formValues: T,
  advancedSearch?: AdvancedSearchFormValues[]
) =>
  React.useCallback(
    (value: number | null, name?: string) => {
      if (!name) return;

      // Prazno polje daje prazan tekst; `String(null)` bi u pretragu upisao riječ „null".
      const stringValue = value === null ? "" : String(value);

      const advancedSearchFormValue: AdvancedSearchFormValues = {
        name,
        value: stringValue,
        text: stringValue,
      };

      const updatedAdvancedSearch = getAdvancedSearch(
        advancedSearchFormValue,
        advancedSearch ?? []
      );

      setFormValues({
        ...formValues,
        advancedSearch: updatedAdvancedSearch,
        [name]: value, // 👈 DECIMAL NUMBER
      });
    },
    [setFormValues, formValues, advancedSearch]
  );



export const useHandleCheckboxChange = <T>(
  setFormValues: React.Dispatch<React.SetStateAction<T>>
) =>
  useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      // This synthetic event is reused for performance reasons.
      // If you must keep the original synthetic event around, use event.persist().

      event.persist();
      setFormValues((old) => ({ ...old, [event.target.name]: checked }));
    },
    [setFormValues]
  );

export const useHandleDropdownChange = <T>(
  setFormValues: (newFormValues: T) => void,
  formValues: T,
  advancedSearch?: AdvancedSearchFormValues[]
) =>
  useCallback(
    (e: any, name: string, item: DropdownOption) => {
      const advancedSearchFormValue: AdvancedSearchFormValues = {
        value: item.value,
        name: name,
        label: e.target.dataset?.label,   // <-- FIXED
        // Oznaka filtera ispisuje tekst; opcija s JSX-om (npr. vozilo) nosi običan naziv u displayName.
        text: typeof item.text === "string" ? item.text : item.displayName ?? "",
        displayName: item.displayName,
      };

      const updatedAdvancedSearch = getAdvancedSearch(
        advancedSearchFormValue,
        advancedSearch ?? []
      );

      setFormValues({
        ...formValues,
        advancedSearch: updatedAdvancedSearch, // Ažuriranje state-a s novim advancedSearch
        [name]: item.value,
      });
    },
    [setFormValues, formValues, advancedSearch]
  );

export const useHandleMultiDropdownChange = <T>(
  setFormValues: (newFormValues: T) => void,
  formValues: T
) =>
  useCallback(
    (name: string, items: MultiDropdownOption[]) => {
      setFormValues({
        ...formValues,
        [name]: items.map((item) => item.value),
      });
    },
    [setFormValues, formValues]
  );

export const useHandleRadioButtonChange = <T>(
  setFormValues: (newFormValues: T) => void,
  formValues: T
) =>
  useCallback(
    (name: string, item: RadioButtonOption) => {
      setFormValues({ ...formValues, [name]: item.value });
    },
    [setFormValues, formValues]
  );

export const useHandleDateChange = <T>(
  setFormValues: (newFormValues: T) => void,
  formValues: T,
  advancedSearch?: AdvancedSearchFormValues[],
  dateLabel?: (name: string) => string | undefined
) =>
  useCallback(
    (name: string, date: Date | null) => {
      if (!advancedSearch) {
        setFormValues({
          ...formValues,
          [name]: date,
        });
        return;
      }

      const dateString = date ? date.toLocaleDateString("hr-HR") : "";

      const advancedSearchFormValue: AdvancedSearchFormValues = {
        name,
        label: dateLabel?.(name),
        value: date ? date.toISOString() : "",
        text: dateString,
      };

      const updatedAdvancedSearch = getAdvancedSearch(
        advancedSearchFormValue,
        advancedSearch
      );

      setFormValues({
        ...formValues,
        advancedSearch: updatedAdvancedSearch,
        [name]: date,
      });
    },
    [setFormValues, formValues, advancedSearch, dateLabel]
  );
